// Central policy for "the API answered 401".
//
// Until 1.26.3 every 401 immediately did setLogout() + a hard navigation to
// /login. That turned a SINGLE failing request into a full sign-out, and in
// production it looked like "logging in does not work": a stale console tab kept
// polling GET /system/status without a token, its 401 wiped the shared
// localStorage JWT (and the JWT cookie) for every tab of the same browser, and
// the operator who had just signed in was thrown back to the login screen.
//
// The rule now is: a 401 is NOT proof that the session is gone. Before anything
// destructive happens the fetch layer re-checks the session once with GET /me,
// and only a session that is REALLY dead ends in sign-out. On top of that:
//
//   * the recovery never runs while a sign-in flow is in progress (the login
//     request, the token being stored and the first navigation are one window in
//     which a late 401 from the previous session must not interfere),
//   * it never runs on the public pages (/login, /signup, …), where a 401 is a
//     normal answer, and
//   * a forced redirect can happen at most once per REDIRECT_COOLDOWN_MS, so no
//     combination of failures can produce a redirect loop.
//
// The decision functions below are pure (no browser, no store) so they can be
// unit-tested; the side effects live in stores/fetch.ts.

// At most one forced redirect to /login per this window (loop breaker). The
// timestamp survives the hard navigation in localStorage.
export const REDIRECT_COOLDOWN_MS = 30_000

// How long the 401 recovery stays suppressed after a sign-in flow starts (and
// again after the token is stored, to cover the first navigation).
export const LOGIN_GRACE_MS = 10_000

const REDIRECT_KEY = 'lastAuthRedirect'

export interface SessionCheckContext {
    // A JWT is present in the store — we believe we are signed in.
    loggedIn: boolean
    // The current location is a public page (no session expected there).
    publicPath: boolean
    // A sign-in flow is running (or just finished) — see LOGIN_GRACE_MS.
    loginInFlight: boolean
}

export interface LogoutDecisionContext extends SessionCheckContext {
    // GET /me answered 200 on the re-check: the session is still good.
    sessionAlive: boolean
    // Epoch ms of the previous forced redirect (0 = never).
    lastRedirectAt: number
    now: number
}

// shouldVerifySession answers whether a 401 is worth a GET /me re-check at all.
// It is also the gate for the destructive part: what is not worth checking is
// never worth signing out for.
export function shouldVerifySession(ctx: SessionCheckContext): boolean {
    return ctx.loggedIn && !ctx.publicPath && !ctx.loginInFlight
}

// shouldForceLogout answers whether this 401 must end in setLogout() + a hard
// navigation to /login: only for a session that the re-check found dead, and at
// most once per REDIRECT_COOLDOWN_MS.
export function shouldForceLogout(ctx: LogoutDecisionContext): boolean {
    if (!shouldVerifySession(ctx)) return false
    if (ctx.sessionAlive) return false
    if (ctx.lastRedirectAt > 0 && ctx.now - ctx.lastRedirectAt < REDIRECT_COOLDOWN_MS) return false
    return true
}

// --- sign-in flow window -----------------------------------------------------

let loginFlowUntil = 0

// beginLoginFlow opens (or extends) the window in which 401 recovery is
// suppressed. Called when a sign-in starts and again once the token is stored.
export function beginLoginFlow(graceMs: number = LOGIN_GRACE_MS): void {
    loginFlowUntil = Date.now() + graceMs
}

export function endLoginFlow(): void {
    loginFlowUntil = 0
}

export function isLoginFlowActive(now: number = Date.now()): boolean {
    return now < loginFlowUntil
}

// --- redirect cooldown (survives the hard navigation) ------------------------

function storage(): Storage | null {
    try {
        return window.localStorage
    } catch {
        return null
    }
}

export function lastForcedRedirectAt(): number {
    const raw = storage()?.getItem(REDIRECT_KEY)
    const at  = Number(raw)
    return Number.isFinite(at) ? at : 0
}

export function markForcedRedirect(now: number = Date.now()): void {
    storage()?.setItem(REDIRECT_KEY, String(now))
}

// clearForcedRedirect is called on a successful sign-in: a fresh session must
// start with a clean cooldown, otherwise a real expiry shortly after would be
// swallowed by the loop breaker.
export function clearForcedRedirect(): void {
    storage()?.removeItem(REDIRECT_KEY)
}
